import katex from 'katex'
import * as React from 'react'

import 'katex/dist/katex.min.css'

import { sanitizeWithDOMPurify } from '@/lib/dompurify'
import { cn } from '@/lib/utils'

export type RichTextareaHandle = {
  insertImage: (url: string, alt?: string, style?: string) => void
  insertMath: (latex: string) => Promise<void>
  updateMathBlock: (node: HTMLElement, latex: string) => Promise<void>
  insertCodeBlock: (code: string, language: string) => void
  hasContent: () => boolean
  focus: () => void
  focusAtEnd: () => void
}

type RichTextareaProps = {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  name?: string
  disabled?: boolean
  classNameWrapper?: string
  className?: string
  maxLength?: number
  onKeyPress?: (e: React.KeyboardEvent<HTMLDivElement>) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void
  onImagePaste?: (file: File) => void
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'onKeyPress' | 'onKeyDown'>

export const RichTextarea = React.forwardRef<RichTextareaHandle, RichTextareaProps>(
  (
    {
      value,
      onChange,
      placeholder,
      name,
      disabled,
      className,
      classNameWrapper,
      maxLength,
      onKeyPress,
      onKeyDown,
      onImagePaste,
      ...rest
    },
    ref
  ) => {
    const divRef = React.useRef<HTMLDivElement | null>(null)
    const lastValidHtmlRef = React.useRef<string>(value || '')
    const isInternalUpdateRef = React.useRef(false)
    const pendingChangeRef = React.useRef<string | null>(null)

    const sanitize = React.useCallback((html: string) => {
      return sanitizeWithDOMPurify(html, {
        USE_PROFILES: { html: true },
        ALLOW_DATA_ATTR: true,
        KEEP_CONTENT: true,
        ALLOWED_ATTR: [
          'class',
          'style',
          'data-latex',
          'data-code',
          'data-language',
          'data-math-rendered',
          'contenteditable',
          'src',
          'alt',
          'id',
          'title',
          'aria-hidden',
          'role',
          'xmlns',
          'display',
          'width',
          'height',
          'viewBox',
          'focusable',
        ],
        ALLOWED_TAGS: [
          'div',
          'span',
          'p',
          'br',
          'img',
          'pre',
          'code',
          'strong',
          'em',
          'u',
          'a',
          'b',
          'i',
          'sup',
          'sub',
          // KaTeX/MathML output
          'math',
          'semantics',
          'mrow',
          'mi',
          'mo',
          'mn',
          'msup',
          'mfrac',
          'mroot',
          'msqrt',
          'msub',
          'msubsup',
          'munderover',
          'munder',
          'mover',
          'mtable',
          'mtr',
          'mtd',
          'mstyle',
          'mspace',
          'annotation',
          'svg',
          'g',
          'path',
          'line',
        ],
      })
    }, [])

    const renderMathBlocks = React.useCallback(async (container: HTMLElement) => {
      const mathBlocks = container.querySelectorAll('.math-block[data-latex]:not([data-math-rendered])')
      if (mathBlocks.length === 0) return

      try {
        const katex = (await import('katex')).default

        const preprocessLatex = (latex: string) => {
          let processed = latex.replace(/\^(\d+)(?![^{])/g, '^{$1}')
          processed = processed.replace(/_(\d+)(?![^{])/g, '_{$1}')
          processed = processed.replace(/\^(\d+)_(\d+)/g, '^{$1}_{$2}')
          processed = processed.replace(/_(\d+)\^(\d+)/g, '_{$1}^{$2}')
          processed = processed.replace(/([a-zA-Z])\^(\d+)/g, '$1^{$2}')
          return processed
        }

        mathBlocks.forEach(block => {
          const latex = block.getAttribute('data-latex') || ''
          if (!latex.trim()) return

          block.setAttribute('data-math-rendered', 'true')

          try {
            const processedLatex = preprocessLatex(latex.trim())
            const renderedHTML = katex.renderToString(processedLatex, {
              throwOnError: false,
              displayMode: true,
            })
            block.innerHTML = renderedHTML
            // Add hidden raw latex to ensure text content exists for submission validation
            const hidden = document.createElement('span')
            hidden.textContent = `$${latex}$`
            hidden.setAttribute('aria-hidden', 'true')
            hidden.setAttribute(
              'style',
              'display:none;position:absolute;left:-9999px;width:0;height:0;overflow:hidden;'
            )
            block.appendChild(hidden)
          } catch (error) {
            block.innerHTML = `<div class="math-error">Error: ${latex}</div>`
          }
        })
      } catch (error) {
        mathBlocks.forEach(block => {
          block.setAttribute('data-math-rendered', 'true')
        })
      }
    }, [])

    // Sync external value changes to the editor
    React.useEffect(() => {
      const el = divRef.current
      if (!el || isInternalUpdateRef.current) return

      const sanitizedValue = sanitize(value || '')
      if (el.innerHTML !== sanitizedValue) {
        // Save current selection
        const selection = window.getSelection()
        const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null
        const wasActive = document.activeElement === el

        el.innerHTML = sanitizedValue
        lastValidHtmlRef.current = sanitizedValue

        // Restore selection if element was active
        if (wasActive && range) {
          try {
            selection?.removeAllRanges()
            selection?.addRange(range)
          } catch (e) {
            // If restoring selection fails, place cursor at end
            placeCaretAtEnd(el)
          }
        }

        // Render math blocks
        requestAnimationFrame(() => {
          if (el) renderMathBlocks(el)
        })
      }
    }, [value, renderMathBlocks, sanitize])

    const textLen = (el: HTMLElement | null) => {
      if (!el) return 0
      const textContent = el.innerText || ''
      const mathBlocks = el.querySelectorAll('.math-block[data-latex]')
      const codeBlocks = el.querySelectorAll('.code-block[data-code]')
      const images = el.querySelectorAll('img')
      return textContent.length + mathBlocks.length + codeBlocks.length + images.length
    }

    const emitChange = React.useCallback(() => {
      const el = divRef.current
      if (!el || isInternalUpdateRef.current) return

      const next = sanitize(el.innerHTML)

      // Check length limits
      if (maxLength && textLen(el) > maxLength) {
        el.innerHTML = lastValidHtmlRef.current
        placeCaretAtEnd(el)
        return
      }

      if (next !== lastValidHtmlRef.current) {
        lastValidHtmlRef.current = next

        // If we have a pending change from insertion, use that instead
        if (pendingChangeRef.current) {
          onChange(pendingChangeRef.current)
          pendingChangeRef.current = null
        } else {
          onChange(next)
        }
      }
    }, [onChange, sanitize, maxLength])

    const onInput = React.useCallback(() => {
      if (!isInternalUpdateRef.current) {
        emitChange()
      }
    }, [emitChange])

    const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault()

      // Check for image files in clipboard
      const items = e.clipboardData.items
      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        if (item.type.indexOf('image') !== -1) {
          const file = item.getAsFile()
          if (file && onImagePaste) {
            // Pass the file to the parent component for handling
            onImagePaste(file)
            return
          } else if (file) {
            // Fallback: convert to base64 for display (if no onImagePaste handler)
            const reader = new FileReader()
            reader.onload = event => {
              const base64Url = event.target?.result as string
              if (base64Url) {
                // Create image element directly
                const img = document.createElement('img')
                img.src = base64Url
                img.alt = 'Pasted image'

                // Set responsive sizing based on screen size
                const isMobile = window.innerWidth <= 768
                const maxSize = isMobile ? '80px' : '100px'
                const margin = isMobile ? '0 2px' : '0 4px'

                img.style.maxWidth = maxSize
                img.style.maxHeight = maxSize
                img.style.display = 'inline-block'
                img.style.verticalAlign = 'middle'
                img.style.margin = margin
                img.style.objectFit = 'contain'
                img.style.border = '1px solid #ccc'
                img.style.borderRadius = '4px'

                // Insert the image element directly
                insertElementAtCursor(img)
                emitChange()
              }
            }
            reader.onerror = () => {
              // Failed to read pasted image file
            }
            reader.readAsDataURL(file)
            return
          }
        }
      }

      // Handle regular text/HTML paste
      const clipboardHTML = e.clipboardData.getData('text/html')
      const clipboardText = e.clipboardData.getData('text/plain')

      // For plain text, wrap in a div to maintain structure
      const htmlToInsert = clipboardHTML || `<div>${clipboardText.replace(/\n/g, '<br>')}</div>`
      const sanitizedHtml = sanitize(htmlToInsert)

      insertHtmlAtCursor(sanitizedHtml)
      emitChange()
    }

    const insertElementAtCursor = (element: HTMLElement) => {
      const el = divRef.current
      if (!el) return false

      // Ensure selection is within the editor
      el.focus()
      const currentSelection = window.getSelection()
      if (!currentSelection || currentSelection.rangeCount === 0 || !el.contains(currentSelection.anchorNode)) {
        const range = document.createRange()
        range.selectNodeContents(el)
        range.collapse(false)
        const sel = window.getSelection()
        sel?.removeAllRanges()
        sel?.addRange(range)
      }

      // Manual insertion
      const selection = window.getSelection()
      if (!selection) return false

      let range: Range
      if (selection.rangeCount > 0) {
        range = selection.getRangeAt(0)
      } else {
        // Create range at end of content
        range = document.createRange()
        range.selectNodeContents(el)
        range.collapse(false)
        selection.addRange(range)
      }

      // Delete current selection
      range.deleteContents()

      // Insert the element
      range.insertNode(element)

      // Insert a space after the image for better cursor placement
      const spaceNode = document.createTextNode(' ')
      range.insertNode(spaceNode)

      // Move cursor to end of inserted content
      range.setStartAfter(spaceNode)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)

      return true
    }

    const insertHtmlAtCursor = (html: string) => {
      const el = divRef.current
      if (!el) return false

      // Ensure selection is within the editor
      el.focus()
      const currentSelection = window.getSelection()
      if (!currentSelection || currentSelection.rangeCount === 0 || !el.contains(currentSelection.anchorNode)) {
        const range = document.createRange()
        range.selectNodeContents(el)
        range.collapse(false)
        const sel = window.getSelection()
        sel?.removeAllRanges()
        sel?.addRange(range)
      }

      // Use document.execCommand for better browser compatibility
      if (document.queryCommandSupported && document.queryCommandSupported('insertHTML')) {
        try {
          document.execCommand('insertHTML', false, html)
          return true
        } catch (e) {
          // Fall back to manual insertion
        }
      }

      // Manual insertion fallback
      const selection = window.getSelection()
      if (!selection) return false

      let range: Range
      if (selection.rangeCount > 0) {
        range = selection.getRangeAt(0)
      } else {
        // Create range at end of content
        range = document.createRange()
        range.selectNodeContents(el)
        range.collapse(false)
        selection.addRange(range)
      }

      // Delete current selection
      range.deleteContents()

      // Create document fragment from HTML
      const template = document.createElement('template')
      template.innerHTML = html.trim()
      const fragment = template.content

      // Insert the fragment
      let lastNode: Node | null = null
      const nodes = Array.from(fragment.childNodes)

      nodes.forEach(node => {
        const clone = node.cloneNode(true)
        range.insertNode(clone)
        lastNode = clone
        range.setStartAfter(clone)
      })

      // Move cursor to end of inserted content
      if (lastNode) {
        range.setStartAfter(lastNode)
        range.collapse(true)
        selection.removeAllRanges()
        selection.addRange(range)
      }

      return true
    }

    const escapeAttr = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

    const placeCaretAtEnd = (el: HTMLElement) => {
      el.focus()
      const range = document.createRange()
      range.selectNodeContents(el)
      range.collapse(false)
      const sel = window.getSelection()
      sel?.removeAllRanges()
      sel?.addRange(range)
    }

    // Prevent editing inside math blocks and emit edit event on click
    React.useEffect(() => {
      const el = divRef.current
      if (!el) return

      const stopInsideMath = (e: Event) => {
        const target = (e.target as HTMLElement | null)?.closest?.('.math-block') as HTMLElement | null
        if (target) {
          e.preventDefault()
          e.stopPropagation()
        }
      }

      const handleKeyDown = (e: KeyboardEvent) => {
        const selection = window.getSelection()
        if (!selection) return

        const isBackspace = e.key === 'Backspace'
        const isDelete = e.key === 'Delete'
        if (!isBackspace && !isDelete) return

        const anchorNode = selection.anchorNode as Node | null
        const focusNode = selection.focusNode as Node | null
        if (!anchorNode || !focusNode) return

        const anchorEl = (anchorNode instanceof Element ? anchorNode : anchorNode.parentElement) as HTMLElement | null
        const focusEl = (focusNode instanceof Element ? focusNode : focusNode.parentElement) as HTMLElement | null
        const anchorMath = anchorEl?.closest?.('.math-block') as HTMLElement | null
        const focusMath = focusEl?.closest?.('.math-block') as HTMLElement | null
        if (anchorMath || focusMath) {
          e.preventDefault()
          e.stopPropagation()
          return
        }

        const container = el
        const range = selection.getRangeAt(0)

        const moveCaretAfter = (node: Node) => {
          const r = document.createRange()
          const after = node.nextSibling
          if (after) {
            r.setStartAfter(after)
          } else {
            r.setStartAfter(node)
          }
          r.collapse(true)
          selection.removeAllRanges()
          selection.addRange(r)
        }
        const moveCaretBefore = (node: Node) => {
          const r = document.createRange()
          r.setStartBefore(node)
          r.collapse(true)
          selection.removeAllRanges()
          selection.addRange(r)
        }

        const node = range.startContainer
        const offset = range.startOffset
        let adjacent: Node | null = null

        if (node.nodeType === Node.TEXT_NODE) {
          if (isBackspace && offset === 0) {
            adjacent = (node.parentNode || node).previousSibling
          } else if (isDelete && offset === (node.textContent || '').length) {
            adjacent = (node.parentNode || node).nextSibling
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const elem = node as Element
          const child = elem.childNodes[offset + (isBackspace ? -1 : 0)] || null
          adjacent = child
        }

        if (adjacent instanceof HTMLElement && adjacent.classList?.contains('math-block')) {
          e.preventDefault()
          e.stopPropagation()
          if (isBackspace) moveCaretBefore(adjacent)
          if (isDelete) moveCaretAfter(adjacent)
          return
        }

        if (node === container) {
          const sib = isBackspace ? container.childNodes[offset - 1] : container.childNodes[offset]
          if (sib instanceof HTMLElement && sib.classList?.contains('math-block')) {
            e.preventDefault()
            e.stopPropagation()
            if (isBackspace) moveCaretBefore(sib)
            if (isDelete) moveCaretAfter(sib)
          }
        }
      }

      const handleClick = (e: MouseEvent) => {
        const target = (e.target as HTMLElement | null)?.closest?.('.math-block') as HTMLElement | null
        if (target) {
          e.preventDefault()
          e.stopPropagation()
          const latex = target.getAttribute('data-latex') || ''
          window.dispatchEvent(
            new CustomEvent('rt-math-edit', {
              detail: { node: target, latex },
            })
          )
        }
      }

      el.addEventListener('click', handleClick)
      el.addEventListener('mousedown', stopInsideMath)
      el.addEventListener('keydown', stopInsideMath)
      el.addEventListener('keydown', handleKeyDown)

      return () => {
        el.removeEventListener('click', handleClick)
        el.removeEventListener('mousedown', stopInsideMath)
        el.removeEventListener('keydown', stopInsideMath)
        el.removeEventListener('keydown', handleKeyDown)
      }
    }, [])

    React.useImperativeHandle(ref, () => ({
      insertImage: (
        url: string,
        alt = '',
        style = 'max-width: 100px; max-height: 100px; display: inline-block; vertical-align: middle; margin: 0 4px;'
      ) => {
        const img = `<img src="${escapeAttr(url)}" alt="${escapeAttr(alt)}" style="${escapeAttr(style)}" />`

        isInternalUpdateRef.current = true
        const success = insertHtmlAtCursor(img)

        if (success) {
          // Get updated content and store for next onChange
          const el = divRef.current
          if (el) {
            const newContent = sanitize(el.innerHTML)
            pendingChangeRef.current = newContent
            lastValidHtmlRef.current = newContent
          }
        }

        setTimeout(() => {
          isInternalUpdateRef.current = false
          emitChange()
        }, 0)
      },

      insertMath: async (latex: string) => {
        const mathBlock = `<div class="math-block" data-latex="${escapeAttr(latex)}" contenteditable="false" style="display: block; background-color: #f8f9fa; border: 1px solid #e9ecef; border-radius: 8px; padding: 12px 16px; margin: 8px 0; cursor: pointer; user-select: none; text-align: center; transition: all 0.2s ease; position: relative; clear: both; width: 100%; box-sizing: border-box; pointer-events: auto;">$$${escapeAttr(latex)}$$</div>`

        isInternalUpdateRef.current = true
        const success = insertHtmlAtCursor(mathBlock)

        if (success) {
          try {
            const el = divRef.current

            if (el) {
              const mathBlocks = el.querySelectorAll('.math-block[data-latex]:not([data-math-rendered])')

              mathBlocks.forEach(block => {
                if (block.getAttribute('data-latex') === latex) {
                  block.setAttribute('data-math-rendered', 'true')

                  try {
                    const processedLatex = latex
                      .replace(/\^(\d+)(?![^{])/g, '^{$1}')
                      .replace(/_(\d+)(?![^{])/g, '_{$1}')
                      .replace(/\^(\d+)_(\d+)/g, '^{$1}_{$2}')
                      .replace(/_(\d+)\^(\d+)/g, '_{$1}^{$2}')
                      .replace(/([a-zA-Z])\^(\d+)/g, '$1^{$2}')

                    const renderedHTML = katex.renderToString(processedLatex, {
                      throwOnError: false,
                      displayMode: true,
                    })
                    block.innerHTML = renderedHTML
                    // Add hidden raw latex for submission validation
                    const hidden = document.createElement('span')
                    hidden.textContent = `$${latex}$`
                    hidden.setAttribute('aria-hidden', 'true')
                    hidden.setAttribute(
                      'style',
                      'display:none;position:absolute;left:-9999px;width:0;height:0;overflow:hidden;'
                    )
                    block.appendChild(hidden)

                    const after = block.nextSibling
                    if (!after || !(after.nodeType === Node.ELEMENT_NODE && (after as HTMLElement).tagName === 'BR')) {
                      const br = document.createElement('br')
                      block.parentNode?.insertBefore(br, after || null)
                    }

                    const range = document.createRange()
                    const sel = window.getSelection()
                    if (block.nextSibling) {
                      range.setStartAfter(block.nextSibling)
                    } else {
                      range.setStartAfter(block)
                    }
                    range.collapse(true)
                    sel?.removeAllRanges()
                    sel?.addRange(range)
                  } catch (error) {
                    block.innerHTML = `<div class="math-error">Error: ${latex}</div>`
                  }
                }
              })

              const newContent = sanitize(el.innerHTML)
              pendingChangeRef.current = newContent
              lastValidHtmlRef.current = newContent
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Math rendering error:', error)
          }
        }

        setTimeout(() => {
          isInternalUpdateRef.current = false
          emitChange()
        }, 100)
      },

      updateMathBlock: async (node: HTMLElement, latex: string) => {
        const el = divRef.current
        if (!el || !node || !el.contains(node)) return

        isInternalUpdateRef.current = true

        node.setAttribute('data-latex', latex)
        node.setAttribute('data-math-rendered', 'true')

        try {
          const processedLatex = latex
            .replace(/\^(\d+)(?![^{])/g, '^{$1}')
            .replace(/_(\d+)(?![^{])/g, '_{$1}')
            .replace(/\^(\d+)_(\d+)/g, '^{$1}_{$2}')
            .replace(/_(\d+)\^(\d+)/g, '_{$1}^{$2}')
            .replace(/([a-zA-Z])\^(\d+)/g, '$1^{$2}')

          const renderedHTML = katex.renderToString(processedLatex, {
            throwOnError: false,
            displayMode: true,
          })
          node.innerHTML = renderedHTML
          // Add hidden raw latex for submission validation
          const hidden = document.createElement('span')
          hidden.textContent = `$${latex}$`
          hidden.setAttribute('aria-hidden', 'true')
          hidden.setAttribute('style', 'display:none;position:absolute;left:-9999px;width:0;height:0;overflow:hidden;')
          node.appendChild(hidden)

          const after = node.nextSibling
          if (!after || !(after.nodeType === Node.ELEMENT_NODE && (after as HTMLElement).tagName === 'BR')) {
            const br = document.createElement('br')
            node.parentNode?.insertBefore(br, after || null)
          }

          const newContent = sanitize(el.innerHTML)
          pendingChangeRef.current = newContent
          lastValidHtmlRef.current = newContent
        } catch (_) {
          // ignore rendering errors
        }

        setTimeout(() => {
          isInternalUpdateRef.current = false
          emitChange()
        }, 0)
      },

      insertCodeBlock: (code: string, language: string) => {
        const codeBlock = `<div class="code-block" data-code="${escapeAttr(code)}" data-language="${escapeAttr(language)}" contenteditable="false" style="display: block; background-color: #282c34; border: 1px solid #444; border-radius: 8px; margin: 8px 0; cursor: pointer; user-select: none; position: relative; clear: both; width: 100%; box-sizing: border-box; pointer-events: auto;"><pre style="margin: 0; padding: 12px; font-family: Monaco, Menlo, Ubuntu Mono, monospace; font-size: 13px; line-height: 1.5; color: #abb2bf; overflow-x: auto; white-space: pre-wrap; word-wrap: break-word; background: #282c34;">${escapeAttr(code)}</pre></div>`

        isInternalUpdateRef.current = true
        const success = insertHtmlAtCursor(codeBlock)

        if (success) {
          // Get updated content and store for next onChange
          const el = divRef.current
          if (el) {
            const newContent = sanitize(el.innerHTML)
            pendingChangeRef.current = newContent
            lastValidHtmlRef.current = newContent
          }
        }

        setTimeout(() => {
          isInternalUpdateRef.current = false
          emitChange()
        }, 0)
      },

      hasContent: () => {
        const el = divRef.current
        if (!el) return false

        const textContent = el.innerText?.trim() || ''
        const mathBlocks = el.querySelectorAll('.math-block[data-latex]')
        const codeBlocks = el.querySelectorAll('.code-block[data-code]')
        const images = el.querySelectorAll('img')

        return textContent.length > 0 || mathBlocks.length > 0 || codeBlocks.length > 0 || images.length > 0
      },

      focus: () => {
        const el = divRef.current
        if (el) {
          el.focus()
          // Only place caret at end if element is empty
          if (!el.innerText.trim()) {
            placeCaretAtEnd(el)
          }
        }
      },
      focusAtEnd: () => {
        const el = divRef.current
        if (el) {
          placeCaretAtEnd(el)
        }
      },
    }))

    return (
      <>
        <style jsx>{`
          .math-block .katex {
            font-size: 1.2em !important;
            line-height: 1.4 !important;
          }
          .math-block .katex-display {
            margin: 0 !important;
            text-align: center !important;
          }
          .math-error {
            color: #dc3545 !important;
            font-style: italic !important;
            background-color: #f8d7da !important;
            padding: 4px 8px !important;
            border-radius: 4px !important;
            border: 1px solid #f5c6cb !important;
          }
          .math-block:hover {
            background-color: #e9ecef !important;
            border-color: #dee2e6 !important;
          }
          .code-block:hover {
            border-color: #666 !important;
          }

          /* Ensure contenteditable elements display properly */
          [contenteditable='true'] {
            outline: none;
          }

          /* Fix for block elements in contenteditable */
          .math-block,
          .code-block {
            display: block !important;
            width: 100% !important;
          }

          /* Ensure images in contenteditable don't break text flow */
          [contenteditable='true'] img {
            max-width: 100px !important;
            max-height: 100px !important;
            display: inline-block !important;
            vertical-align: middle !important;
            margin: 0 4px !important;
            object-fit: contain !important;
            border-radius: 4px !important;
            border: 1px solid #e5e7eb !important;
          }

          /* Mobile-specific adjustments for images */
          @media (max-width: 768px) {
            [contenteditable='true'] img {
              max-width: 80px !important;
              max-height: 80px !important;
              margin: 0 2px !important;
            }
          }
        `}</style>
        <div className={cn('relative', classNameWrapper)}>
          <div
            ref={divRef}
            role="textbox"
            aria-multiline="true"
            contentEditable={!disabled}
            suppressContentEditableWarning
            onInput={onInput}
            onPaste={onPaste}
            onKeyPress={onKeyPress}
            onKeyDown={onKeyDown}
            className={cn(
              'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
              'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'whitespace-pre-wrap break-words break-all [overflow-wrap:anywhere] [word-break:break-word]',
              'max-w-full overflow-x-hidden overflow-y-auto',
              'min-h-[60px]', // Minimum height for better UX
              className
            )}
            {...rest}
          />
          {(() => {
            const v = value || ''
            const hasBlocks = v.includes('class="math-block"') || v.includes('class="code-block"') || /<img\b/i.test(v)
            const plainText = v.replace(/<[^>]*>/g, '').trim()
            const hasAnyFromValue = hasBlocks || plainText.length > 0
            const hasAnyFromDom = divRef.current ? textLen(divRef.current) > 0 : false
            return placeholder && !(hasAnyFromValue || hasAnyFromDom)
          })() && (
            <span className="pointer-events-none absolute left-3 top-2 text-sm text-muted-foreground">
              {placeholder}
            </span>
          )}
          {name ? <input type="hidden" name={name} value={value} /> : null}
        </div>
      </>
    )
  }
)

RichTextarea.displayName = 'RichTextarea'
